import { inviteError } from './invite-errors';

export const ROOM_INVITE_STORAGE = 'jt.room-invite.v1';
const VALID_TOKEN = /^[A-Za-z0-9_-]{43}$/;
interface StoragePort { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }
export interface InviteHandoffState { phase: 'none' | 'pending' | 'redeeming' | 'error'; error: string; retryable: boolean }
export interface InviteHandoffPort {
  ensureAccount(signal: AbortSignal): Promise<unknown>;
  redeem(token: string, signal: AbortSignal): Promise<{ roomId: string }>;
  navigate(roomId: string): void;
}

/** The secret is private, never part of the subscribable/rendered snapshot. */
export class InviteHandoff {
  private token: string | null = null;
  private generation = 0;
  private listeners = new Set<(s: InviteHandoffState) => void>();
  private state: InviteHandoffState = { phase: 'none', error: '', retryable: false };
  constructor(private storage?: StoragePort) {
    try {
      const stored = storage?.getItem(ROOM_INVITE_STORAGE);
      if (stored && VALID_TOKEN.test(stored)) this.token = stored;
      else if (stored !== null && stored !== undefined) storage?.removeItem(ROOM_INVITE_STORAGE);
    } catch { /* memory-only */ }
    if (this.token) this.state = { phase: 'pending', error: '', retryable: true };
  }
  subscribe(fn: (s: InviteHandoffState) => void): () => void {
    this.listeners.add(fn); fn(this.state);
    return () => { this.listeners.delete(fn); };
  }
  get snapshot(): InviteHandoffState { return this.state; }
  private set(state: InviteHandoffState): void { this.state = state; for (const fn of this.listeners) fn(state); }
  capture(href: string, replace: (path: string) => void): void {
    const url = new URL(href);
    const fragment = new URLSearchParams(url.hash.slice(1));
    if (!fragment.has('roomInvite')) return;
    const token = fragment.get('roomInvite');
    fragment.delete('roomInvite');
    url.hash = fragment.toString();
    // Before any await, navigation, account request, or rendering.
    replace(url.pathname + url.search + url.hash);
    this.generation += 1;
    this.token = token !== null && VALID_TOKEN.test(token) ? token : null;
    try {
      if (this.token) this.storage?.setItem(ROOM_INVITE_STORAGE, this.token);
      else this.storage?.removeItem(ROOM_INVITE_STORAGE);
    } catch { /* A denied storage API still permits same-page login. */ }
    this.set(this.token ? { phase: 'pending', error: '', retryable: true } : {
      phase: 'error', error: 'This invitation link is invalid or incomplete. Ask the owner for a new one.', retryable: false
    });
  }
  dismiss(): void {
    this.generation += 1;
    this.clearToken();
    this.set({ phase: 'none', error: '', retryable: false });
  }
  private clearToken(): void {
    this.token = null;
    try { this.storage?.removeItem(ROOM_INVITE_STORAGE); } catch { /* memory-only */ }
  }
  async resume(gateOpen: boolean, port: InviteHandoffPort): Promise<void> {
    if (!gateOpen || !this.token || this.state.phase === 'redeeming') return;
    const token = this.token;
    const generation = this.generation;
    this.set({ phase: 'redeeming', error: '', retryable: false });
    try {
      const signal = AbortSignal.timeout(20_000);
      await port.ensureAccount(signal);
      if (generation !== this.generation) return;
      const result = await port.redeem(token, signal);
      if (generation !== this.generation) return;
      if (!result.roomId) throw new Error('Missing room');
      this.clearToken();
      port.navigate(result.roomId);
      this.set({ phase: 'none', error: '', retryable: false });
    } catch (error) {
      if (generation !== this.generation) return;
      const status = (error as { status?: number })?.status;
      const retryable = status !== 400 && status !== 403 && status !== 404 && status !== 410;
      if (!retryable) this.clearToken();
      this.set({ phase: 'error', error: inviteError(error), retryable });
    }
  }
}

let storage: Storage | undefined;
try { storage = globalThis.sessionStorage; } catch { /* memory-only */ }
export const roomInvitation = new InviteHandoff(storage);

export function captureRoomInvitation(): void {
  if (typeof location === 'undefined') return;
  roomInvitation.capture(location.href, path => history.replaceState(history.state, '', path));
}
